/** A notification's settings change (backend notification-settings plan §4.3-4.5, P3), as the
 *  requester's preview recorded it (`preview_summary.settings`): per `notification.update` item,
 *  whether it still sends, the upcoming bookings it reaches before → after, its structure, the
 *  stored settings diff (the change-set page's `DiffTable`), every channel added or changed with
 *  the bookings it sends for, the tenant's notifications whose tour-specific replacement moves, the
 *  franchise effect, and the event move with the backend's warnings. Shown on the proposal review
 *  page, so the person applying it sees who starts and stops receiving what.
 *
 *  Ops read the franchise parent's notifications, so they get the names; the dashboard contract's
 *  per-viewer `redacted` shape (counts and a note) is handled too. A summary is JSON an earlier
 *  preview stored: a part missing from it leaves the page — the only place to apply — standing. */
import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';

import type {
  AIProposalSettingsChannel,
  AIProposalSettingsDiffRow,
  AIProposalSettingsEntry,
  AIProposalSettingsFranchise,
} from '@/api/aiEdit';
import { Field, FieldGrid, SectionCard } from '@/components/ReadView';

import { DiffTable, type DiffRow } from './AIChangeEntries';
import { Lines, plural, type Line } from './FranchiseCopySummary';

type Entry = AIProposalSettingsEntry;
/** As stored: an older or malformed record may lack any part. */
type Stored = Partial<Entry>;
type ChipColor = 'default' | 'info' | 'warning';

const SENDING: Record<Entry['sending']['change'], { label: string; color: ChipColor }> = {
  starts: { label: 'Starts sending', color: 'info' },
  stops: { label: 'Stops sending', color: 'warning' },
  'keeps sending': { label: 'Keeps sending', color: 'default' },
  'stays silent': { label: 'Stays silent', color: 'default' },
};

/** What a diff field means where its name alone is cryptic. */
const FIELD_NOTE: Record<string, string> = { atag: 'access tags' };

const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

function Caption({ text }: { text: string }) {
  return (
    <Typography variant="caption" color="text.secondary" component="div">
      {text}
    </Typography>
  );
}

/** "12 → 0"; a count at the preview's row cap reads "500+". */
function beforeAfter(before: number, after: number, capped = false): string {
  const top = Math.max(before, after);
  const fmt = (v: number) => (capped && v === top ? `${v}+` : `${v}`);
  return `${fmt(before)} → ${fmt(after)}`;
}

const named = (rows: { id: number; name: string }[]) =>
  rows.map((r) => `${r.name} (#${r.id})`).join(', ');

function recipients(r: AIProposalSettingsChannel['recipients']): string {
  if (!r) return '';
  if (r.addresses?.length) return r.addresses.join(', ');
  if (r.number) return r.number;
  if (r.support_contact) return `${r.support_contact.name} (#${r.support_contact.id})`;
  if (r.hidden_reference) return 'a support contact not shown';
  return '';
}

/** "on · Address: ops@acme.test · structured" — never more than the settings read shows. */
function channelState(c: AIProposalSettingsChannel): string {
  const parts = [c.enabled ? 'on' : 'off'];
  if (c.integration) return [...parts, 'integration (read-only)'].join(' · ');
  const destination = c.destination_label || c.destination;
  const to = recipients(c.recipients);
  if (destination) parts.push(to ? `${destination}: ${to}` : destination);
  else if (to) parts.push(to);
  if (c.structured !== null && c.structured !== undefined) {
    parts.push(c.structured ? 'structured' : 'bespoke');
  }
  return parts.join(' · ');
}

/** The diff as `DiffTable` rows: the settings fields, and the channels apart. */
function diffRows(diff: AIProposalSettingsDiffRow[]): { fields: DiffRow[]; channels: DiffRow[] } {
  const fields: DiffRow[] = [];
  const channels: DiffRow[] = [];
  diff.forEach((row, i) => {
    const key = `${row.field}-${i}`;
    switch (row.field) {
      case 'channels': {
        const b = row.before ?? null;
        const a = row.after ?? null;
        channels.push({
          key,
          label: (
            <>
              {row.id !== null && row.id !== undefined
                ? `${row.channel} #${row.id}`
                : `${row.channel} (new)`}
              <Caption
                text={`${row.change === 'added' ? 'Added' : 'Updated'} · bookings ${beforeAfter(
                  n(row.bookings?.before),
                  n(row.bookings?.after),
                )}`}
              />
              {row.note && <Caption text={row.note} />}
            </>
          ),
          before: b ? channelState(b) : null,
          after: a ? channelState(a) : null,
          beforePresent: b !== null,
          afterPresent: a !== null,
        });
        break;
      }
      case 'tours':
      case 'tags':
      case 'roles':
      case 'atag': {
        const added = row.added ?? [];
        const removed = row.removed ?? [];
        const note = FIELD_NOTE[row.field];
        fields.push({
          key,
          label: (
            <>
              {row.field}
              {note && <Caption text={note} />}
              {n(row.not_shown) > 0 && <Caption text={`${n(row.not_shown)} not shown`} />}
            </>
          ),
          before: removed.length ? `removed: ${named(removed)}` : null,
          after: added.length ? `added: ${named(added)}` : null,
          beforePresent: removed.length > 0,
          afterPresent: added.length > 0,
        });
        break;
      }
      case 'rules':
        fields.push({
          key,
          label: (
            <>
              rules
              <Caption
                text={`${n(row.added)} added · ${n(row.removed)} removed · ${n(row.changed)} changed`}
              />
            </>
          ),
          before: plural(n(row.before), 'row', 'rows'),
          after: plural(n(row.after), 'row', 'rows'),
          beforePresent: true,
          afterPresent: true,
        });
        break;
      default:
        // A scalar -- or a field this console does not know yet: its values as they are.
        fields.push({
          key,
          label: String(row.field),
          before: row.before,
          after: row.after,
          beforePresent: 'before' in row,
          afterPresent: 'after' in row,
        });
    }
  });
  return { fields, channels };
}

const structureWord = (s: { structured?: boolean; mixed?: boolean } | undefined) =>
  !s ? '—' : s.structured ? 'Structured' : s.mixed ? 'Mixed' : 'Bespoke';

function siblingLines(siblings: Entry['siblings']): Line[] {
  const tours = (rows: { id: number; name: string | null }[]) =>
    rows.map((t) => t.name ?? `tour #${t.id}`).join(', ');
  const bookings = (count: number, one: string, many: string) =>
    `${plural(count, 'upcoming booking', 'upcoming bookings')} ${count === 1 ? one : many}`;
  return siblings.flatMap((s) => {
    const head = `#${s.id} ${s.name} · ${s.event}`;
    const lines: Line[] = [];
    if (s.silenced_tours?.length) {
      lines.push({
        key: `${s.id}:${s.event}:silenced`,
        text: `${head} — now replaced on ${tours(s.silenced_tours)} · ${bookings(n(s.bookings?.silenced), 'stops receiving it', 'stop receiving it')}`,
      });
    }
    if (s.unsilenced_tours?.length) {
      lines.push({
        key: `${s.id}:${s.event}:unsilenced`,
        text: `${head} — no longer replaced on ${tours(s.unsilenced_tours)} · ${bookings(n(s.bookings?.unsilenced), 'receives it again', 'receive it again')}`,
      });
    }
    return lines;
  });
}

function FranchiseEffect({ franchise }: { franchise: AIProposalSettingsFranchise }) {
  if (franchise.role === 'franchise_parent') {
    return (
      <Box data-settings-franchise>
        <FieldGrid>
          <Field
            label="Licensees reached"
            value={`${beforeAfter(n(franchise.reached?.before), n(franchise.reached?.after))} of ${n(franchise.licensees)}`}
          />
          <Field
            label="Licensee bookings (upcoming)"
            value={beforeAfter(
              n(franchise.licensee_bookings?.before),
              n(franchise.licensee_bookings?.after),
              franchise.licensee_bookings?.capped,
            )}
          />
        </FieldGrid>
      </Box>
    );
  }
  const silencedLabel = 'Franchise parent’s notifications it silences';
  const freedLabel = 'Franchise parent’s notifications it frees';
  if (franchise.redacted) {
    return (
      <Box data-settings-franchise data-settings-franchise-redacted>
        {franchise.note && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {franchise.note}
          </Typography>
        )}
        <Lines
          label={silencedLabel}
          lines={[
            {
              key: 'silenced',
              text: plural(n(franchise.counts?.silenced), 'notification', 'notifications'),
            },
          ]}
        />
        <Lines
          label={freedLabel}
          lines={[
            {
              key: 'unsilenced',
              text: plural(n(franchise.counts?.unsilenced), 'notification', 'notifications'),
            },
          ]}
        />
      </Box>
    );
  }
  const rows = (list: Extract<AIProposalSettingsFranchise, { role: 'licensee' }>['silenced']) =>
    (list ?? []).map((p) => ({
      key: `${p.id}`,
      text: `#${p.id} ${p.name} · ${p.event} · ${plural(n(p.bookings), 'booking', 'bookings')}`,
    }));
  return (
    <Box data-settings-franchise>
      <Lines label={silencedLabel} lines={rows(franchise.silenced)} />
      <Lines label={freedLabel} lines={rows(franchise.unsilenced)} />
    </Box>
  );
}

function Warnings({ entry }: { entry: Stored }) {
  const move = entry.event_move;
  const warnings = entry.warnings ?? [];
  if (!move && warnings.length === 0) return null;
  const side = (s: { code: string; timing: string | null } | undefined) =>
    s ? `${s.code}${s.timing ? ` (${s.timing})` : ''}` : '—';
  const counted =
    move && typeof move.get_nothing === 'number' && typeof move.may_receive_twice === 'number';
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }} data-settings-warnings>
      {move && (
        <Box data-settings-event-move>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {`Event moves: ${side(move.from)} → ${side(move.to)}.`}
          </Typography>
          {counted && (
            <Typography variant="body2">
              {`Now: ${plural(n(move.get_nothing), 'booking', 'bookings')} ${n(move.get_nothing) === 1 ? 'gets' : 'get'} nothing, ${n(move.may_receive_twice)} may receive it twice.`}
            </Typography>
          )}
        </Box>
      )}
      {warnings.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: move ? 0.5 : 0, pl: 2.5 }}>
          {warnings.map((w, i) => (
            <Typography key={`${i}:${w}`} component="li" variant="body2">
              {w}
            </Typography>
          ))}
        </Box>
      )}
    </Alert>
  );
}

function SettingsItem({ entry }: { entry: Stored }) {
  const label = entry.label || entry.notification?.name || `Item #${entry.item ?? '?'}`;
  const ids = [
    entry.notification ? `notification #${entry.notification.id}` : null,
    entry.item !== undefined ? `item #${entry.item}` : null,
  ].filter(Boolean);
  const sending = entry.sending ? SENDING[entry.sending.change] : undefined;
  const { fields, channels } = diffRows(entry.diff ?? []);
  const reach = entry.reach;
  const structured = entry.structured;
  const siblings = siblingLines(entry.siblings ?? []);
  return (
    <Box data-settings-item={entry.item}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" component="h3">
          {label}
        </Typography>
        {ids.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {ids.join(' · ')}
          </Typography>
        )}
        {sending && (
          <Chip size="small" variant="outlined" color={sending.color} label={sending.label} />
        )}
      </Stack>
      <Warnings entry={entry} />
      <FieldGrid>
        {reach && (
          <Field
            label="Reach (upcoming bookings)"
            value={
              <>
                <Typography variant="body2" component="div">
                  {beforeAfter(n(reach.before), n(reach.after), reach.capped)}
                </Typography>
                <Typography variant="body2" component="div">
                  {`${n(reach.start)} start · ${n(reach.stop)} stop receiving it`}
                </Typography>
                {reach.basis && <Caption text={reach.basis} />}
              </>
            }
          />
        )}
        {structured && (
          <Field
            label="Structure"
            value={
              structureWord(structured.before) === structureWord(structured.after)
                ? structureWord(structured.after)
                : `${structureWord(structured.before)} → ${structureWord(structured.after)}`
            }
          />
        )}
      </FieldGrid>
      {fields.length > 0 && <DiffTable title="Settings diff" rows={fields} />}
      {channels.length > 0 && <DiffTable title="Channels" rows={channels} />}
      {siblings.length > 0 && (
        <Lines label="Siblings: tour-specific replacement moves" lines={siblings} />
      )}
      {entry.franchise && <FranchiseEffect franchise={entry.franchise} />}
    </Box>
  );
}

export function SettingsChangeSummary({ settings }: { settings: AIProposalSettingsEntry[] }) {
  return (
    <Box data-settings-changes>
      <SectionCard title="Settings">
        {settings.map((entry, i) => (
          <Fragment key={entry.item ?? `i${i}`}>
            {i > 0 && <Divider sx={{ my: 2 }} />}
            <SettingsItem entry={entry} />
          </Fragment>
        ))}
      </SectionCard>
    </Box>
  );
}
